import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canUserPerform } from '../lib/permissions';
import type { PermissionKey } from '../lib/permissions';
import StatusScreen from './StatusScreen';

export interface RequireRoleProps {
  /** Key from the PERMISSIONS table in `src/lib/permissions.ts`. */
  permission: PermissionKey;
  children: ReactNode;
  /** Rendered instead of the default panel when the permission is missing. */
  fallback?: ReactNode;
}

/**
 * Renders `children` only when the signed-in user's role satisfies
 * `permission`, as decided by `canUserPerform()`.
 *
 * This is a usability guard, not a security boundary: the backend re-checks the
 * same role table on every mutating request. Its job is to stop users from
 * walking into a screen whose actions would be rejected.
 */
export default function RequireRole({ permission, children, fallback }: RequireRoleProps) {
  const { user } = useAuth();

  if (canUserPerform(user?.role, permission)) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return (
    <StatusScreen
      tone="alert"
      kicker="Доступ ограничен"
      title="Недостаточно прав"
      description="У вас нет прав для этого действия. Обратитесь к администратору."
      action={
        <Link className="button button--primary" to="/agents">
          К списку агентов
        </Link>
      }
    />
  );
}
